import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Verbose cart refresh tracing. Filter DevTools console by `[developly-cart]`.
 * Silence: `sessionStorage.setItem('developly_cart_debug','0')` — enable: `'1'` or omit key.
 */
function developlyCartDebugEnabled() {
  try {
    const v = sessionStorage.getItem('developly_cart_debug');
    if (v === '0') return false;
  } catch {
    /* ignore */
  }
  return true;
}

/**
 * @param {string} phase
 * @param {Record<string, unknown>} [data]
 */
function dclog(phase, data) {
  if (!developlyCartDebugEnabled()) return;
  console.info('[developly-cart]', phase, data ?? {});
}

/**
 * @param {Record<string, string | null | undefined> | undefined} sections
 */
function sectionKeysSummary(sections) {
  if (!sections || typeof sections !== 'object') return { keys: [], nullish: [] };
  const keys = Object.keys(sections);
  const nullish = keys.filter((k) => sections[k] == null || sections[k] === '');
  return { keys, nullish };
}

/**
 * @param {Response} res
 * @returns {Promise<Record<string, unknown> | null>}
 */
async function readCartJsonResponse(res, logLabel = 'cart') {
  const ct = res.headers.get('content-type') || '';
  const text = await res.text();
  if (!res.ok) {
    dclog('readCartJson: http error', {
      label: logLabel,
      status: res.status,
      contentType: ct,
      bodyPreview: text.slice(0, 400),
    });
    console.warn('developly-cart HTTP', res.status, text.slice(0, 240));
    return null;
  }
  if (!ct.includes('json') && !text.trimStart().startsWith('{')) {
    dclog('readCartJson: not json', {
      label: logLabel,
      contentType: ct,
      bodyPreview: text.slice(0, 400),
    });
    console.warn('developly-cart: expected JSON body');
    return null;
  }
  try {
    const parsed = /** @type {Record<string, unknown>} */ (JSON.parse(text));
    dclog('readCartJson: ok', {
      label: logLabel,
      item_count: parsed.item_count,
      ...sectionKeysSummary(
        /** @type {Record<string, string | null | undefined>} */ (parsed.sections)
      ),
    });
    return parsed;
  } catch {
    dclog('readCartJson: parse error', { label: logLabel, contentType: ct, bodyPreview: text.slice(0, 200) });
    console.warn('developly-cart: invalid JSON');
    return null;
  }
}

/** Max section IDs per Section Rendering API request (Shopify limit). */
const DEVELOPLY_SECTION_RENDER_BATCH = 5;

// Sourced from Liquid (snippets/scripts.liquid) so this cannot drift from the
// server-rendered notice; the literal is only a fallback. See product-form.js.
const CART_LIMIT_CENTS = (typeof Theme !== 'undefined' && Theme.cartLimitCents) || 40000;

/**
 * `sections_url` for bundled cart + section rendering — include search so pagination/filters match.
 * @returns {string}
 */
function developlyCartSectionsUrl() {
  return window.location.pathname + window.location.search;
}

/**
 * Collect all developly cart section IDs (drawer + page + placeholders) for Cart API `sections` param.
 * @returns {string[]}
 */
function getDeveloplySectionIds() {
  /** @type {string[]} */
  const ids = [];
  for (const el of document.querySelectorAll('[data-developly-cart-section-id]')) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.dataset.developlySkip === 'true') continue;
    const id = el.dataset.developlyCartSectionId;
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/**
 * Gift rules from each `developly-cart-root` (merged; duplicate variant+threshold keys deduped).
 * @returns {{ variantId: number; thresholdCents: number; available: boolean }[]}
 */
function getDeveloplyGiftRules() {
  /** @type {Map<string, { variantId: number; thresholdCents: number; available: boolean }>} */
  const byKey = new Map();
  for (const root of document.querySelectorAll('developly-cart-root')) {
    const script = root.querySelector('script[type="application/json"][data-developly-gift-rules]');
    if (!script?.textContent) continue;
    try {
      const parsed = JSON.parse(script.textContent.trim());
      if (!Array.isArray(parsed)) continue;
      for (const rule of parsed) {
        const vid = Number(rule.variantId);
        const th = Number(rule.thresholdCents) || 0;
        if (!vid || th < 1) continue;
        const key = `${vid}-${th}`;
        byKey.set(key, {
          variantId: vid,
          thresholdCents: th,
          available: rule.available !== false,
        });
      }
    } catch {
      /* ignore malformed JSON */
    }
  }
  // v8: Theme settings > Free gift in the bag (snippets/dk-v8-head.liquid)
  const dkGift = /** @type {any} */ (window).DK?.gift;
  if (dkGift?.on && dkGift.variantId && dkGift.thresholdCents > 0) {
    const vid = Number(dkGift.variantId);
    const th = Number(dkGift.thresholdCents);
    byKey.set(`${vid}-${th}`, { variantId: vid, thresholdCents: th, available: dkGift.available !== false });
  }
  return [...byKey.values()];
}

/**
 * Bag subtotal that counts toward a gift: every non-gift line at its price before codes.
 * @param {Record<string, any>} cart
 */
function developlyQualifyingSubtotal(cart) {
  let sum = 0;
  for (const item of cart.items || []) {
    if (isDeveloplyGiftCartItem(item)) continue;
    sum += Number(item.original_line_price ?? item.line_price ?? 0);
  }
  return sum;
}

/* ---------- v8: keep the sale code on the cart ---------- */
const DK_BF_REMOVED = 'dk_bf_removed';
let dkBfTriedKey = '';

function dkBfConfig() {
  const bf = /** @type {any} */ (window).DK?.bf;
  return bf && bf.live && bf.auto && bf.code ? bf : null;
}

/**
 * /discount/<code> only sticks to a cart that has items: once the bag has lines and carries no code,
 * put the sale code on it (unless the shopper removed it this session). Returns the Cart API
 * response (with sections) when it changed the cart, else null. One try per cart state.
 * @returns {Promise<Record<string, any> | null>}
 */
async function dkEnsureSaleCode() {
  const bf = dkBfConfig();
  if (!bf) return null;
  try {
    if (sessionStorage.getItem(DK_BF_REMOVED)) return null;
  } catch {
    /* storage blocked */
  }
  const cart = /** @type {Record<string, any> | null} */ (await fetchDeveloplyCartJs());
  if (!cart || !cart.item_count) return null;
  if (Array.isArray(cart.discount_codes) && cart.discount_codes.length) return null;
  const key = `${cart.token}:${cart.item_count}:${cart.total_price}`;
  if (dkBfTriedKey === key) return null;
  dkBfTriedKey = key;
  const ids = getDeveloplySectionIds();
  try {
    const res = await fetch(Theme.routes.cart_update_url, {
      ...fetchConfig('json', {
        body: JSON.stringify({ discount: bf.code, sections: ids.join(','), sections_url: developlyCartSectionsUrl() }),
      }),
    });
    const data = await readCartJsonResponse(res, 'dkEnsureSaleCode');
    if (!data || data.errors || data.status) return null;
    document.documentElement.classList.add('dk-bf-applied');
    return data;
  } catch {
    return null;
  }
}

/**
 * @returns {Promise<Record<string, unknown> | null>}
 */
async function fetchDeveloplyCartJs() {
  try {
    const base = Theme.routes.cart_url.replace(/\/$/, '');
    const res = await fetch(`${base}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    return /** @type {Record<string, unknown>} */ (await res.json());
  } catch {
    return null;
  }
}

/**
 * @param {Record<string, unknown>} item
 */
function isDeveloplyGiftCartItem(item) {
  const props = item?.properties;
  if (!props || typeof props !== 'object') return false;
  const rec = /** @type {Record<string, unknown>} */ (props);
  return rec._developly_gift === '1' || rec['_developly_gift'] === '1';
}

/**
 * @param {Record<string, unknown>} data
 */
function dispatchDeveloplyGiftCartUpdate(data) {
  document.dispatchEvent(
    new CartUpdateEvent(data, 'developly-cart-gift-sync', {
      itemCount: /** @type {number} */ (data.item_count ?? 0),
      source: 'developly-cart-gift-sync',
      sections: /** @type {Record<string, string>} */ (data.sections),
    })
  );
}

/**
 * @param {number} line
 * @param {number} quantity
 * @param {string[]} ids
 */
async function developlyGiftApplyChangeLine(line, quantity, ids) {
  const body = JSON.stringify({
    line,
    quantity,
    sections: ids.join(','),
    sections_url: developlyCartSectionsUrl(),
  });
  const res = await fetch(Theme.routes.cart_change_url, {
    ...fetchConfig('json', { body }),
  });
  const data = await readCartJsonResponse(res, 'giftSync:changeLine');
  if (!data || data.errors) return;
  dispatchDeveloplyGiftCartUpdate(data);
}

/**
 * @param {number} variantId
 * @param {string[]} ids
 */
async function developlyGiftApplyAdd(variantId, ids) {
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', '1');
  formData.append('properties[_developly_gift]', '1');
  formData.append('sections', ids.join(','));
  formData.append('sections_url', developlyCartSectionsUrl());
  const res = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: formData,
  });
  const data = await readCartJsonResponse(res, 'giftSync:add');
  if (!data || data.status) return;
  dispatchDeveloplyGiftCartUpdate(data);
}

let developlyGiftSyncTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
let developlyGiftSyncInFlight = false;

function scheduleDeveloplyGiftSync() {
  if (developlyGiftSyncTimer) clearTimeout(developlyGiftSyncTimer);
  developlyGiftSyncTimer = setTimeout(() => {
    developlyGiftSyncTimer = null;
    void runDeveloplyGiftSync();
  }, 48);
}

async function runDeveloplyGiftSync() {
  const rules = getDeveloplyGiftRules();
  if (rules.length === 0) return;
  const ids = getDeveloplySectionIds();
  if (ids.length === 0) return;
  if (developlyGiftSyncInFlight) {
    scheduleDeveloplyGiftSync();
    return;
  }

  developlyGiftSyncInFlight = true;
  try {
    let cart = await fetchDeveloplyCartJs();
    if (!cart || !Array.isArray(cart.items)) return;

    /** @type {Set<number>} */
    const variantIds = new Set(rules.map((r) => r.variantId));

    for (const vid of variantIds) {
      const subtotal = developlyQualifyingSubtotal(cart);
      const rulesForVariant = rules.filter((r) => r.variantId === vid);
      const shouldHave = rulesForVariant.some(
        (r) => subtotal >= r.thresholdCents && r.available
      );

      const idx = cart.items.findIndex(
        (item) =>
          item.variant_id === vid && isDeveloplyGiftCartItem(/** @type {Record<string, unknown>} */ (item))
      );
      const hasGiftLine = idx >= 0;
      const lineNum = idx + 1;

      if (!shouldHave && hasGiftLine) {
        await developlyGiftApplyChangeLine(lineNum, 0, ids);
        cart = await fetchDeveloplyCartJs();
        if (!cart || !Array.isArray(cart.items)) return;
        continue;
      }

      if (shouldHave && !hasGiftLine) {
        await developlyGiftApplyAdd(vid, ids);
        cart = await fetchDeveloplyCartJs();
        if (!cart || !Array.isArray(cart.items)) return;
      }
    }
  } finally {
    developlyGiftSyncInFlight = false;
  }
}

/**
 * Morph every developly section present in a Cart API `sections` map (e.g. after add to cart).
 * @param {Record<string, string | null | undefined> | undefined} sections
 */
function morphDeveloplySectionsFromResponse(sections) {
  const domIds = getDeveloplySectionIds();
  if (!sections || typeof sections !== 'object') {
    dclog('morph: skip (no sections object)', { domIds });
    return;
  }
  const summary = sectionKeysSummary(sections);
  dclog('morph: start', {
    domIds,
    responseKeys: summary.keys,
    responseNullishKeys: summary.nullish,
  });
  const morphed = new Set();
  const shopifyId = (/** @type {string} */ sid) => `shopify-section-${sid}`;
  const wasOpen = document.querySelector('developly-cart-root[data-developly-drawer-open]') !== null;
  for (const el of document.querySelectorAll('[data-developly-cart-section-id]')) {
    if (!(el instanceof HTMLElement) || el.dataset.developlySkip === 'true') continue;
    const sid = el.dataset.developlyCartSectionId;
    if (!sid || morphed.has(sid)) continue;
    const html = sections[sid];
    const len = typeof html === 'string' ? html.length : 0;
    const hasDomRoot = !!document.getElementById(shopifyId(sid));
    if (html) {
      try {
        morphSection(sid, html);
        morphed.add(sid);
        dclog('morph: ok', { sid, htmlLength: len, hadShopifySectionInDom: hasDomRoot });
      } catch (err) {
        dclog('morph: threw', { sid, htmlLength: len, hadShopifySectionInDom: hasDomRoot, error: String(err) });
        console.warn('developly-cart morph', sid, err);
      }
    } else {
      dclog('morph: no html for sid', { sid, hadShopifySectionInDom: hasDomRoot });
    }
  }
  if (wasOpen) restoreDeveloplyDrawerOpen();
}

/** A morph re-renders the drawer closed; put it back open without moving focus. */
function restoreDeveloplyDrawerOpen() {
  for (const root of document.querySelectorAll('developly-cart-root')) {
    if (!(root instanceof HTMLElement) || root.dataset.presentation === 'page') continue;
    const backdrop = root.querySelector('[data-developly-cart-backdrop]');
    const panel = root.querySelector('[data-developly-cart-panel]');
    if (!(backdrop instanceof HTMLElement) || !(panel instanceof HTMLElement)) continue;
    root.setAttribute('data-developly-drawer-open', '');
    backdrop.hidden = false;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    backdrop.setAttribute('data-open', '');
    panel.setAttribute('data-open', '');
  }
}

/**
 * Refetch developly cart section markup via Section Rendering API (current page context).
 * Use when bundled `sections` from Cart Ajax is missing, null, or incomplete.
 * @see https://shopify.dev/docs/api/ajax/section-rendering
 */
async function fetchDeveloplySectionsViaRenderingApi() {
  const ids = getDeveloplySectionIds();
  if (ids.length === 0) {
    dclog('fallback sections API: no dom ids', {});
    return;
  }

  dclog('fallback sections API: start', { ids, batches: Math.ceil(ids.length / DEVELOPLY_SECTION_RENDER_BATCH) });

  for (let i = 0; i < ids.length; i += DEVELOPLY_SECTION_RENDER_BATCH) {
    const batch = ids.slice(i, i + DEVELOPLY_SECTION_RENDER_BATCH);
    const url = new URL(window.location.href);
    url.searchParams.set('sections', batch.join(','));
    url.searchParams.set('_dc', String(Date.now()));

    const res = await fetch(url.toString(), {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const ct = res.headers.get('content-type') || '';
    dclog('fallback sections API: fetch done', {
      ok: res.ok,
      status: res.status,
      contentType: ct,
      batch,
      pathAndQuery: url.pathname + url.search,
    });
    if (!res.ok) continue;

    if (!ct.includes('application/json')) continue;

    let data;
    try {
      data = await res.json();
    } catch (e) {
      dclog('fallback sections API: json parse failed', { batch, error: String(e) });
      continue;
    }
    morphDeveloplySectionsFromResponse(/** @type {Record<string, string>} */ (data));
  }
}

/**
 * @param {Record<string, string | null> | undefined} sections
 * @returns {boolean}
 */
function developlySectionsResponseIncomplete(sections) {
  const ids = getDeveloplySectionIds();
  if (ids.length === 0) return false;
  if (!sections || typeof sections !== 'object') return true;
  return ids.some((id) => {
    const html = sections[id];
    return html == null || html === '';
  });
}

/**
 * Theme cart events use `Event` subclasses (`CartAddEvent`, `CartUpdateEvent`) with a `detail` bag — not `CustomEvent`.
 * @param {Event} e
 * @returns {Record<string, unknown> | undefined}
 */
function getCartEventData(e) {
  if (!('detail' in e) || !e.detail || typeof e.detail !== 'object') return undefined;
  const data = /** @type {{ data?: Record<string, unknown> }} */ (e.detail).data;
  return data && typeof data === 'object' ? data : undefined;
}

let developlyMorphListenerAttached = false;
if (!developlyMorphListenerAttached) {
  developlyMorphListenerAttached = true;
  document.addEventListener(ThemeEvents.cartUpdate, async (e) => {
    if (!(e instanceof Event)) return;
    const data = getCartEventData(e);
    const sections = /** @type {Record<string, string | null> | undefined} */ (data?.sections);
    const incomplete = developlySectionsResponseIncomplete(sections);
    const domIds = getDeveloplySectionIds();
    dclog('cart:update listener', {
      source: data?.source,
      itemCount: data?.itemCount,
      domIds,
      incomplete,
      ...sectionKeysSummary(sections),
    });
    const ensured = await dkEnsureSaleCode();
    if (ensured) {
      morphDeveloplySectionsFromResponse(ensured.sections);
    } else {
      morphDeveloplySectionsFromResponse(sections);
    }

    // Always refetch fresh drawer markup: the HTML bundled with the add can lag one add behind
    // (count right, drawer showing the previous cart)
    {
      try {
        await fetchDeveloplySectionsViaRenderingApi();
      } catch (err) {
        dclog('cart:update fallback threw', { error: String(err) });
        console.warn('developly-cart: section rendering fallback failed', err);
      }
    }

    scheduleDeveloplyGiftSync();
  });
}

class DeveloplyCartRoot extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {Element | null} */
  #focusBeforeOpen = null;

  /** @type {number} */
  #busyDepth = 0;

  connectedCallback() {
    this.#presentation = this.dataset.presentation === 'page' ? 'page' : 'drawer';
    this.#sectionId = this.dataset.developlyCartSectionId || '';
    this.#autoOpen = this.dataset.autoOpen === 'true';

    if (this.#presentation === 'drawer') {
      document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
      document.addEventListener('click', this.#onDocumentClick, true);
      document.addEventListener('keydown', this.#onKeydown);
    }

    this.addEventListener('click', this.#onRootClick);
    this.addEventListener('keydown', this.#onRootKeydown);
    this.addEventListener('input', this.#onRootInput);

    if (this.#presentation === 'drawer' || !document.querySelector('developly-cart-root[data-presentation="drawer"]')) {
      void dkEnsureSaleCode().then((data) => {
        if (data) morphDeveloplySectionsFromResponse(data.sections);
      });
    }

    scheduleDeveloplyGiftSync();
  }

  /** Typing a different code turns "Applied" back into "Apply". @param {Event} e */
  #onRootInput = (e) => {
    if (!(e.target instanceof HTMLInputElement) || !e.target.matches('[data-developly-promo-input]')) return;
    const btn = this.querySelector('[data-dk-promo-applied]');
    if (!(btn instanceof HTMLElement)) return;
    const same = e.target.value.trim().toUpperCase() === (btn.dataset.code || '').toUpperCase();
    btn.textContent = same ? 'Applied' : 'Apply';
    btn.classList.toggle('is-applied', same);
  };

  /** @param {KeyboardEvent} e */
  #onRootKeydown = (e) => {
    if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement) || !e.target.matches('[data-developly-promo-input]')) return;
    e.preventDefault();
    const code = e.target.value.trim();
    if (code && !this.#isBusy) this.#applyDiscount(code, e.target);
  };

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('click', this.#onRootClick);
    this.removeEventListener('keydown', this.#onRootKeydown);
    this.removeEventListener('input', this.#onRootInput);
    this.#unlockScroll();
  }

  /** @type {'drawer' | 'page'} */
  #presentation = 'drawer';

  /** @type {string} */
  #sectionId = '';

  /** @type {boolean} */
  #autoOpen = false;

  /** @type {AbortController | null} */
  #pickAbort = null;

  get #isOpen() {
    const panel = this.querySelector('[data-developly-cart-panel]');
    return panel instanceof HTMLElement && panel.hasAttribute('data-open');
  }

  get #isBusy() {
    return this.#busyDepth > 0;
  }

  #pushBusy() {
    this.#busyDepth += 1;
    this.dataset.developlyBusy = 'true';
  }

  #popBusy() {
    this.#busyDepth = Math.max(0, this.#busyDepth - 1);
    if (this.#busyDepth === 0) {
      delete this.dataset.developlyBusy;
    }
  }

  #lockScroll() {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  #unlockScroll() {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  open() {
    if (this.#presentation !== 'drawer') return;
    const backdrop = this.querySelector('[data-developly-cart-backdrop]');
    const panel = this.querySelector('[data-developly-cart-panel]');
    if (!(backdrop instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    this.#focusBeforeOpen = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.setAttribute('data-developly-drawer-open', '');

    backdrop.hidden = false;
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      backdrop.setAttribute('data-open', '');
      panel.setAttribute('data-open', '');
      requestAnimationFrame(() => {
        const closeBtn = this.querySelector('[data-developly-cart-close]');
        if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
      });
    });
    this.#lockScroll();
  }

  close() {
    if (this.#presentation !== 'drawer') return;
    const backdrop = this.querySelector('[data-developly-cart-backdrop]');
    const panel = this.querySelector('[data-developly-cart-panel]');
    if (!(backdrop instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    backdrop.removeAttribute('data-open');
    panel.removeAttribute('data-open');
    panel.setAttribute('aria-hidden', 'true');

    const done = () => {
      backdrop.hidden = true;
      panel.hidden = true;
      backdrop.removeEventListener('transitionend', done);
      this.removeAttribute('data-developly-drawer-open');
    };
    backdrop.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 350);
    this.#unlockScroll();

    const prev = this.#focusBeforeOpen;
    this.#focusBeforeOpen = null;
    if (prev instanceof HTMLElement && document.contains(prev)) {
      requestAnimationFrame(() => prev.focus({ preventScroll: true }));
    }
  }

  /** @param {Event} e */
  #onCartAdd = (e) => {
    if (!this.#autoOpen || this.#presentation !== 'drawer') return;
    if (getCartEventData(e)?.didError) return;
    this.open();
  };

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    if (e.key !== 'Escape' || !this.#isOpen) return;
    e.preventDefault();
    this.close();
  };

  /** @param {MouseEvent} e */
  #onDocumentClick = (e) => {
    if (!(e.target instanceof Element)) return;
    const trigger = e.target.closest('[data-developly-cart-open]');
    if (trigger) {
      e.preventDefault();
      this.open();
    }
  };

  /** @param {MouseEvent} e */
  #onRootClick = (e) => {
    if (!(e.target instanceof Element)) return;

    if (e.target.closest('[data-developly-cart-close]') || e.target.closest('[data-developly-cart-backdrop]')) {
      e.preventDefault();
      this.close();
      return;
    }

    const pick = e.target.closest('[data-developly-upsell-pick]');
    if (pick instanceof HTMLElement) {
      const productUrl = pick.dataset.productUrl;
      const productTitle = pick.dataset.productTitle || '';
      if (productUrl) void this.#openProductQuickAdd(productUrl, productTitle);
      return;
    }

    if (this.#isBusy) return;

    const dec = e.target.closest('[data-developly-qty-dec]');
    const inc = e.target.closest('[data-developly-qty-inc]');
    const remove = e.target.closest('[data-developly-remove]');
    const upsell = e.target.closest('[data-developly-upsell-add]');
    const promoToggle = e.target.closest('[data-developly-promo-toggle]');
    const promoApply = e.target.closest('[data-developly-promo-apply]');

    if (dec instanceof HTMLElement) {
      const line = parseInt(dec.dataset.line || '0', 10);
      const row = this.querySelector(`[data-developly-line-row="${line}"]`);
      const qtyEl = row?.querySelector(`[data-line-qty="${line}"]`);
      const q = parseInt(qtyEl?.textContent || '1', 10);
      if (line) this.#changeLine(line, Math.max(0, q - 1));
      return;
    }

    if (inc instanceof HTMLElement) {
      const line = parseInt(inc.dataset.line || '0', 10);
      const row = this.querySelector(`[data-developly-line-row="${line}"]`);
      const qtyEl = row?.querySelector(`[data-line-qty="${line}"]`);
      const q = parseInt(qtyEl?.textContent || '1', 10);
      if (line) this.#changeLine(line, q + 1);
      return;
    }

    if (remove instanceof HTMLElement) {
      const line = parseInt(remove.dataset.line || '0', 10);
      if (line) this.#changeLine(line, 0);
      return;
    }

    if (upsell instanceof HTMLElement) {
      const vid = upsell.dataset.variantId;
      if (vid) this.#addVariant(vid, upsell);
      return;
    }


    if (promoToggle instanceof HTMLElement) {
      const wrap = this.querySelector('[data-developly-promo-toggle-wrap]');
      const form = this.querySelector('[data-developly-promo-form]');
      wrap?.classList.add('hidden');
      form?.classList.remove('hidden');
      const input = this.querySelector('[data-developly-promo-input]');
      if (input instanceof HTMLInputElement) input.focus();
      return;
    }

    const promoRemove = e.target.closest('[data-dk-promo-remove]');
    if (promoRemove instanceof HTMLElement) {
      const bf = dkBfConfig();
      const input = this.querySelector('[data-developly-promo-input]');
      const current = input instanceof HTMLInputElement ? input.value.trim().toUpperCase() : '';
      if (bf && current === String(bf.code).toUpperCase()) {
        try {
          sessionStorage.setItem(DK_BF_REMOVED, '1');
        } catch {
          /* storage blocked */
        }
        document.documentElement.classList.remove('dk-bf-applied');
      }
      this.#applyDiscount('', promoRemove);
      return;
    }

    const promoApplied = e.target.closest('[data-dk-promo-applied]');
    if (promoApplied instanceof HTMLElement) {
      const input = this.querySelector('[data-developly-promo-input]');
      const code = input instanceof HTMLInputElement ? input.value.trim() : '';
      const applied = (promoApplied.getAttribute('aria-label') || '').toUpperCase();
      if (code && !applied.includes(` ${code.toUpperCase()} `)) this.#applyDiscount(code, promoApplied);
      return;
    }

    if (promoApply instanceof HTMLElement) {
      const input = this.querySelector('[data-developly-promo-input]');
      const code = input instanceof HTMLInputElement ? input.value.trim() : '';
      if (code) this.#applyDiscount(code, promoApply);
      return;
    }
  };

  /**
   * @param {number} line
   * @param {number} quantity
   */
  async #changeLine(line, quantity) {
    const ids = getDeveloplySectionIds();
    if (ids.length === 0) return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    this.#pushBusy();
    const body = JSON.stringify({
      line,
      quantity,
      sections: ids.join(','),
      sections_url: developlyCartSectionsUrl(),
    });

    try {
      dclog('changeLine: request', {
        url: Theme.routes.cart_change_url,
        line,
        quantity,
        sectionsRequested: ids,
        sections_url: developlyCartSectionsUrl(),
      });
      const res = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', { body }),
        signal: this.#abort.signal,
      });
      const data = await readCartJsonResponse(res, 'changeLine');
      if (!data) return;
      if (data.errors) {
        dclog('changeLine: cart errors', { errors: data.errors });
        console.warn('developly-cart change', data.errors);
        return;
      }

      dclog('changeLine: dispatch CartUpdateEvent', {
        item_count: data.item_count,
        ...sectionKeysSummary(
          /** @type {Record<string, string | null | undefined>} */ (data.sections)
        ),
      });
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || 'developly-cart', {
          itemCount: /** @type {number} */ (data.item_count ?? 0),
          source: 'developly-cart',
          sections: /** @type {Record<string, string>} */ (data.sections),
        })
      );
    } catch (err) {
      if (/** @type {Error} */ (err).name !== 'AbortError') console.error(err);
    } finally {
      this.#popBusy();
    }
  }

  /**
   * @param {string} variantId
   * @param {HTMLElement} [trigger]
   */
  async #addVariant(variantId, trigger) {
    const ids = getDeveloplySectionIds();
    if (ids.length === 0) return;

    try {
      const cartCheckRes = await fetch('/cart.js');
      const currentCart = await readCartJsonResponse(cartCheckRes, 'cartLimitCheck');
      if (currentCart && typeof currentCart.total_price === 'number' && currentCart.total_price > CART_LIMIT_CENTS) {
        dclog('addVariant: cart limit reached', { total_price: currentCart.total_price });
        // Localized in Liquid (snippets/scripts.liquid) so the amount uses the market's
        // currency format. If it is unavailable, leave the button label untouched rather
        // than blanking it.
        const limitLabel = (typeof Theme !== 'undefined' && Theme.translations?.cart_limit_short) || '';
        if (trigger && limitLabel) {
          const originalText = trigger.textContent;
          trigger.textContent = limitLabel;
          setTimeout(() => { if (trigger) trigger.textContent = originalText; }, 2500);
        }
        return;
      }
    } catch {
      // Proceed if cart check fails
    }

    this.#pushBusy();
    if (trigger) trigger.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', ids.join(','));
    formData.append('sections_url', developlyCartSectionsUrl());

    try {
      dclog('addVariant: request', {
        url: Theme.routes.cart_add_url,
        variantId,
        sectionsRequested: ids,
        sections_url: developlyCartSectionsUrl(),
      });
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await readCartJsonResponse(res, 'addVariant');
      if (!data) return;
      if (data.status) {
        dclog('addVariant: error status', { status: data.status, message: data.message });
        console.warn('developly-cart add', data);
        return;
      }

      dclog('addVariant: dispatch CartUpdateEvent', {
        item_count: data.item_count,
        ...sectionKeysSummary(
          /** @type {Record<string, string | null | undefined>} */ (data.sections)
        ),
      });
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || 'developly-cart', {
          itemCount: /** @type {number} */ (data.item_count ?? 0),
          source: 'developly-cart',
          sections: /** @type {Record<string, string>} */ (data.sections),
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
      this.#popBusy();
    }
  }

  /**
   * @param {string} code
   * @param {HTMLElement} [trigger]
   */
  async #applyDiscount(code, trigger) {
    const ids = getDeveloplySectionIds();
    if (ids.length === 0) return;

    const errEl = this.querySelector('[data-developly-promo-error]');
    errEl?.classList.add('hidden');
    const bfCfg = dkBfConfig();
    if (bfCfg && code && code.toUpperCase() === String(bfCfg.code).toUpperCase()) {
      try {
        sessionStorage.removeItem(DK_BF_REMOVED);
      } catch {
        /* storage blocked */
      }
    }

    this.#pushBusy();
    if (trigger) trigger.setAttribute('aria-busy', 'true');
    // Code on the bag before this try: a rejected code must not knock it off (Shopify replaces codes)
    const prevCode = this.querySelector('[data-dk-promo-applied]')?.getAttribute('data-code') || '';

    const body = JSON.stringify({
      discount: code,
      sections: ids.join(','),
      sections_url: developlyCartSectionsUrl(),
    });

    try {
      dclog('applyDiscount: request', {
        url: Theme.routes.cart_update_url,
        sectionsRequested: ids,
        sections_url: developlyCartSectionsUrl(),
      });
      const res = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body }),
      });
      const data = await readCartJsonResponse(res, 'applyDiscount');
      if (!data) {
        dclog('applyDiscount: empty response', {});
        if (errEl instanceof HTMLElement) {
          errEl.textContent = this.dataset.i18nDiscountFailed || '';
          errEl.classList.remove('hidden');
        }
        return;
      }

      const badDiscount =
        Array.isArray(data.discount_codes) &&
        data.discount_codes.some(
          (/** @type {{ code: string; applicable: boolean }} */ d) =>
            d.code?.toLowerCase() === code.toLowerCase() && d.applicable === false
        );
      if (data.errors || data.status || badDiscount) {
        dclog('applyDiscount: rejected', { errors: data.errors, status: data.status, badDiscount });
        const notApplicable = this.dataset.i18nDiscountNotApplicable || '';
        const invalid = this.dataset.i18nDiscountInvalid || '';
        const msg =
          typeof data.errors === 'string'
            ? data.errors
            : /** @type {string} */ (data.message) ||
              (badDiscount ? notApplicable : invalid);
        if (errEl instanceof HTMLElement) {
          errEl.textContent = msg;
          errEl.classList.remove('hidden');
        }
        if (prevCode && prevCode.toLowerCase() !== code.toLowerCase()) {
          await fetch(Theme.routes.cart_update_url, {
            ...fetchConfig('json', { body: JSON.stringify({ discount: prevCode }) }),
          }).catch(() => {});
        }
        return;
      }

      dclog('applyDiscount: dispatch CartUpdateEvent', {
        item_count: data.item_count,
        ...sectionKeysSummary(
          /** @type {Record<string, string | null | undefined>} */ (data.sections)
        ),
      });
      document.dispatchEvent(
        new CartUpdateEvent(data, this.#sectionId || 'developly-cart', {
          itemCount: /** @type {number} */ (data.item_count ?? 0),
          source: 'developly-cart-discount',
          sections: /** @type {Record<string, string>} */ (data.sections),
        })
      );
    } catch (err) {
      console.error(err);
    } finally {
      if (trigger) trigger.removeAttribute('aria-busy');
      this.#popBusy();
    }
  }
  /**
   * @param {string} productUrl
   * @param {string} productTitle
   */
  async #openProductQuickAdd(productUrl, productTitle) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogEl = document.getElementById('quick-add-dialog');
    if (!modalContent || !dialogEl) return;

    this.#pickAbort?.abort();
    this.#pickAbort = new AbortController();
    this.#pushBusy();

    try {
      const res = await fetch(productUrl, { signal: this.#pickAbort.signal });
      if (!res.ok) return;

      const html = new DOMParser().parseFromString(await res.text(), 'text/html');
      const gridContent = html.querySelector('[data-product-grid-content]');
      if (!gridContent) return;

      if (window.innerWidth < 750) {
        const productDetails = gridContent.querySelector('.product-details');
        const productFormComponent = gridContent.querySelector('product-form-component');
        const variantPicker = gridContent.querySelector('variant-picker');
        const productPrice = gridContent.querySelector('product-price');

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');
        const titleLink = document.createElement('a');
        titleLink.textContent = productTitle;
        titleLink.href = productUrl;
        productHeader.appendChild(titleLink);
        if (productPrice) productHeader.appendChild(productPrice);
        gridContent.appendChild(productHeader);
        if (variantPicker) gridContent.appendChild(variantPicker);
        if (productFormComponent) gridContent.appendChild(productFormComponent);
        productDetails?.remove();
      }

      morph(modalContent, gridContent);

      if (typeof /** @type {Record<string, unknown>} */ (dialogEl).showDialog === 'function') {
        /** @type {Record<string, unknown>} */ (dialogEl).showDialog();
      } else {
        dialogEl.querySelector('dialog')?.showModal();
      }
    } catch (err) {
      if (/** @type {Error} */ (err).name !== 'AbortError') console.error(err);
    } finally {
      this.#pickAbort = null;
      this.#popBusy();
    }
  }
}

dclog('module loaded', {
  cart_change_url: Theme.routes.cart_change_url,
  cart_update_url: Theme.routes.cart_update_url,
  cart_add_url: Theme.routes.cart_add_url,
  developlyDomIds: getDeveloplySectionIds(),
});

if (!customElements.get('developly-cart-root')) {
  customElements.define('developly-cart-root', DeveloplyCartRoot);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleDeveloplyGiftSync(), { once: true });
} else {
  scheduleDeveloplyGiftSync();
}
